import { reloadPageWithNotification, shouldAttemptChunkReload } from "common/utils/chunkReloadGuard";
import { isChunkLoadError } from "common/utils/isChunkLoadError";
import { type ComponentType, type ReactNode, lazy, Suspense } from "react";

interface LazySuspenseOptions {
  fallback?: ReactNode;
}

/**
 * Retry a dynamic import once if it fails with a chunk-load error, then reload
 * the page if the retry also fails. Covers the gap where Vite's preload helper
 * isn't used (render-time fetches from React.lazy) so `vite:preloadError` never
 * fires. Non-chunk errors (syntax, runtime init) are re-thrown immediately.
 */
function importWithRetry<T>(factory: () => Promise<T>): Promise<T> {
  return factory().catch((error: unknown) => {
    if (!isChunkLoadError(error)) {
      throw error;
    }
    // Single retry -- the first failure may be a transient network blip.
    return factory().catch((retryError: unknown) => {
      // Both attempts failed. Reload to pick up fresh chunk URLs (post-deploy).
      if (shouldAttemptChunkReload()) {
        reloadPageWithNotification("Detected repeated chunk-load failure in lazySuspense importWithRetry");
      }
      throw retryError;
    });
  });
}

/**
 * Drop-in replacement for React.lazy() that wraps the component in a Suspense
 * boundary. This prevents the white-flash bug where Vite code-splits lazy
 * chunks and suspension bubbles up to the app root.
 *
 * On chunk-load failure (e.g. stale CDN after a deploy), retries once then
 * reloads the page.
 *
 * Usage:
 *   const MyComponent = lazySuspense(() => import("./MyComponent"));
 *   const Heavy = lazySuspense(() => import("./Heavy"), { fallback: <Spinner /> });
 */
export default function lazySuspense<P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>,
  options: LazySuspenseOptions = {},
): ComponentType<P> {
  // eslint-disable-next-line custom-rules/no-bare-lazy -- This IS the lazySuspense implementation
  const LazyComponent = lazy(() => importWithRetry(factory) as Promise<{ default: ComponentType<P> }>);

  function SuspenseWrapped(props: P) {
    return (
      <Suspense fallback={options.fallback}>
        <LazyComponent {...props} />
      </Suspense>
    );
  }

  return SuspenseWrapped;
}
